/**
 * @fileoverview BrandLoader for loading brand-specific modules and customizations.
 * 
 * This loader handles modules that are specific to particular brands within the system,
 * enabling brand-specific functionality, styling, and business logic based on the
 * detected concept ID and brand configuration.
 * 
 * @author Roshan Jose
 * @version 2.0.0
 * @since 2025
 */

import { RouterHelper } from "../router-helper";
import { IBrandLoader, ModuleLoaderStatus } from "../interfaces/module-loader.interfaces";

/**
 * BrandLoader manages brand-specific modules and customizations.
 * 
 * This loader is responsible for:
 * - Detecting the current brand from concept ID in the DOM
 * - Mapping concept IDs to brand codes using a comprehensive lookup table
 * - Loading brand-specific modules and functionality
 * - Providing brand-aware module loading capabilities
 * - Supporting multiple brands with different customizations
 * 
 * The BrandLoader follows the singleton pattern and implements the IBrandLoader
 * interface for consistent behavior across the router system.
 * 
 * Supported Brands (19 total):
 * - ASV (Concept ID: 3), DVW (24), FSP (4), MDG (5), GUY (6)
 * - HMS (25), JUK (30), LAP (29), MLY (1), MOJ (18)
 * - MRA (8), MRE (9), MRH (2), MRR (10), PLG (14)
 * - PDS (27), RBW (11), SHG (26), WDG (16)
 * 
 * Currently Implemented Brands:
 * - MLY (Molly): Complete implementation with dedicated modules
 * - HMS: Complete implementation with dedicated modules
 * - Additional brands can be easily added with new case statements
 * 
 * Features:
 * - Singleton pattern for global access
 * - Dynamic brand detection via DOM concept ID
 * - Comprehensive concept ID to brand mapping
 * - Secure module loading via RouterHelper
 * - Extensible architecture for new brands
 * - Graceful handling of unknown brands
 * 
 * @class BrandLoader
 * @implements {IBrandLoader}
 */
export class BrandLoader implements IBrandLoader {
    /** Singleton instance of the BrandLoader */
    private static instance: BrandLoader;
    
    /** RouterHelper instance for secure module loading operations */
    private readonly routerHelper: RouterHelper = RouterHelper.getInstance();
    
    /** Current concept ID detected from the DOM */
    private conceptId: string = '';
    
    /** Current brand code derived from the concept ID */
    private currentBrand: string = '';
    
    /** Current status and state of the BrandLoader */
    private readonly status: ModuleLoaderStatus = {
        isInitialized: false,
        loadedModules: [],
        failedModules: [],
        isLoading: false
    };
    
    /**
     * Comprehensive mapping of concept IDs to brand codes.
     * 
     * This map contains all 19 supported brands in the system, providing
     * a centralized lookup table for concept ID to brand code conversion.
     * Each entry maps a concept ID string to a function that returns the brand code.
     * 
     * Supported mappings:
     * - '3' → 'ASV', '24' → 'DVW', '4' → 'FSP', '5' → 'MDG', '6' → 'GUY'
     * - '25' → 'HMS', '30' → 'JUK', '29' → 'LAP', '1' → 'MLY', '18' → 'MOJ'
     * - '8' → 'MRA', '9' → 'MRE', '2' → 'MRH', '10' → 'MRR', '14' → 'PLG'
     * - '27' → 'PDS', '11' → 'RBW', '26' → 'SHG', '16' → 'WDG'
     */
    private readonly conceptIDMap = new Map<string, () => string>([
        ['3', () => 'ASV'],
        ['24', () => 'DVW'],
        ['4', () => 'FSP'],
        ['5', () => 'MDG'],
        ['6', () => 'GUY'],
        ['25', () => 'HMS'],
        ['30', () => 'JUK'],
        ['29', () => 'LAP'],
        ['1', () => 'MLY'],
        ['18', () => 'MOJ'],
        ['8', () => 'MRA'],
        ['9', () => 'MRE'],
        ['2', () => 'MRH'],
        ['10', () => 'MRR'],
        ['14', () => 'PLG'],
        ['27', () => 'PDS'],
        ['11', () => 'RBW'],
        ['26', () => 'SHG'],
        ['16', () => 'WDG'],
    ]);
    
    /**
     * Gets the singleton instance of BrandLoader.
     * Creates a new instance if one doesn't exist.
     * @returns The singleton BrandLoader instance
     */
    static getInstance(): BrandLoader {
        this.instance ??= new BrandLoader();
        return this.instance;
    }    
    
    /**
     * Initializes the BrandLoader by detecting the current brand.
     * 
     * Retrieves the concept ID from the DOM element 'conceptCode' and uses
     * the conceptIDMap to determine the corresponding brand code. This
     * brand detection drives all subsequent brand-specific module loading.
     * 
     * @returns Promise that resolves when initialization is complete
     * @throws {Error} When brand detection or initialization fails
     */
    async init(): Promise<void> {
        try {
            this.conceptId = (document.getElementById('conceptId') as HTMLInputElement)?.value ?? "";
            this.currentBrand = this.conceptIDMap.get(this.conceptId)?.() ?? '';
            this.status.isInitialized = true;
        } catch (error) {
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        }
    }

    /**
     * Returns the human-readable name of this loader.
     * Used for logging, debugging, and status reporting.
     * @returns The loader's display name
     */
    getName(): string {
        return 'BrandLoader';
    }

    /**
     * Returns a copy of the current loader status.
     * Provides real-time information about the loader's state.
     * @returns Current loader status information
     */
    getStatus(): ModuleLoaderStatus {
        return { ...this.status };
    }

    /**
     * Gets the current brand code.
     * 
     * Returns the brand code that was detected during initialization,
     * derived from the concept ID mapping.
     * 
     * @returns The current brand code (e.g., 'MLY', 'HMS', 'ASV')
     */
    getCurrentBrand(): string {
        return this.currentBrand;
    }

    /**
     * Gets the current concept ID.
     * 
     * Returns the concept ID that was detected from the DOM during
     * initialization, used for brand determination.
     * 
     * @returns The current concept ID
     */
    getConceptId(): string {
        return this.conceptId;
    }

    /**
     * Loads all brand-specific modules (delegates to brandModules).
     * 
     * This is the main entry point for loading brand modules,
     * implementing the IModuleLoader interface contract.
     * 
     * @returns Promise that resolves when all brand modules are loaded
     */
    async loadModules(): Promise<void> {
        return this.brandModules();
    }

    /**
     * Loads modules specific to the detected brand.
     * 
     * Uses a switch statement to load brand-specific modules based on the
     * current brand code. Each brand can have its own dedicated modules
     * and functionality.
     * 
     * Currently Implemented Brands:
     * - MLY (Molly): Loads molly-specific modules and functionality
     * - HMS: Loads HMS-specific modules and functionality
     * 
     * Additional brands can be easily added by:
     * 1. Adding a new case to the switch statement
     * 2. Implementing the corresponding module import
     * 3. Adding appropriate error handling
     * 
     * Gracefully handles unknown brands by skipping module loading.
     * 
     * @returns Promise that resolves when brand modules are loaded
     * @throws {Error} When brand module loading fails
     */
    async brandModules(): Promise<void> {
        if (!this.currentBrand) {
            this.routerHelper.routerConsoleLog('No brand detected, skipping brand module loading', false, 'BrandLoader');
            return;
        }

        this.status.isLoading = true;
        
        try {
            switch (this.currentBrand) {
                case 'MLY':
                    await this.routerHelper.loadModuleSafely(
                        'mly',
                        () => import(/* webpackChunkName: "molly-loader" */ '../../mly/index'),
                        'Molly'
                    );                 
                    this.status.loadedModules.push('mly');
                    break;
                case 'HMS':
                    await this.routerHelper.loadModuleSafely(
                        'hms',
                        () => import(/* webpackChunkName: "hms-loader" */ '../../hms/index'),
                        'HMS'
                    );
                    this.status.loadedModules.push('hms');
                    break;
                    default: 
                    break;
            }
        } catch (error) {
            this.status.failedModules.push(this.currentBrand);
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        } finally {
            this.status.isLoading = false;
        }
    }
}
